/**
 * How long to wait during an auth redirect before assuming it has stalled and
 * showing a fallback error to the user. Normally the browser navigates to
 * Keycloak well within this window, so the timeout never fires.
 */
export const AUTH_REDIRECT_TIMEOUT_MS = 10_000;

export interface AuthRedirectTimeoutOptions {
  /** Called when the redirect has not completed within the timeout. */
  onTimeout: () => void;
  /** Timeout before firing. Defaults to {@link AUTH_REDIRECT_TIMEOUT_MS}. */
  timeoutMs?: number;
}

export interface AuthRedirectTimeoutController {
  /** Start the timeout. No-op if already running. */
  start: () => void;
  /** Cancel a pending timeout. Safe to call when nothing is scheduled. */
  cancel: () => void;
}

/**
 * Creates a controller that fires `onTimeout` if an auth redirect does not take
 * the user away from the page within the timeout window. Used to replace an
 * indefinitely-held boot loader with an actionable fallback when
 * `authentication.login()` fails to navigate.
 */
export function createAuthRedirectTimeout(
  options: AuthRedirectTimeoutOptions
): AuthRedirectTimeoutController {
  const { onTimeout, timeoutMs = AUTH_REDIRECT_TIMEOUT_MS } = options;

  let timer: ReturnType<typeof setTimeout> | null = null;

  const cancel = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const start = () => {
    // Ignore re-entrant starts so an in-flight timer keeps its original deadline.
    if (timer !== null) return;
    timer = setTimeout(() => {
      timer = null;
      onTimeout();
    }, timeoutMs);
  };

  return { start, cancel };
}
